// Cash Planning: day by day, what must be in the FNB Gold Business Account and
// on which date, and what Uber will pay in and when.
//
// The rule this page keeps above all: money already earned and money only
// forecast are never blended. Every Uber figure is Received, Earned (awaiting
// payout) or Forecast, named every time, and forecast money never counts toward
// payroll cover. A figure with no source reads Amount unknown with what it needs,
// never R0.
//
// Every figure comes from cashplan_page_v1 (db/migrations/0344); the page
// arranges it. CLAUDE.md rule 4. Open to finance plus can_manage_bank_details.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, RefreshCw, ShieldAlert, Wallet } from 'lucide-react'
import { useGetCashPlan, useGetCashPlanAccess } from '../hooks/backend/banking'
import { Button } from '../lib/shadcn/button'
import { Spinner } from '../lib/shadcn/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../lib/shadcn/tabs'
import { TopStrip } from '../components/cashplan/TopStrip'
import { Ledger } from '../components/cashplan/Ledger'
import { Weekly } from '../components/cashplan/Weekly'
import { DebitVariance, PetrolPattern, UberVariance } from '../components/cashplan/Variance'
import { FreshnessList, PlanTable, RecurringDetected, SettingsForm } from '../components/cashplan/Plan'
import { BalanceDialog, OverrideDialog, RowDialog, type RowDraft } from '../components/cashplan/Dialogs'
import { formatDayYear, formatSast, type CashPlan, type ObligationItem } from '../utils/cashPlan'

const TABS = ['ledger', 'weekly', 'variance', 'plan', 'settings'] as const
type Tab = (typeof TABS)[number]

export default function CashPlanning() {
  const accessQuery = useGetCashPlanAccess<{ allowed: boolean }>()
  const planQuery = useGetCashPlan<CashPlan>()
  const [params, setParams] = useSearchParams()
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'ledger'
  const [balanceOpen, setBalanceOpen] = useState(false)
  const [editing, setEditing] = useState<{ item: ObligationItem; date: string } | null>(null)
  const [rowDraft, setRowDraft] = useState<RowDraft | null>(null)

  const loadAccess = accessQuery.trigger
  const loadPlan = planQuery.trigger
  useEffect(() => { void loadAccess({}, { skipCache: true }).catch(() => undefined) }, [loadAccess])
  const allowed = accessQuery.data?.allowed === true
  const refresh = useCallback(() => { void loadPlan({}, { skipCache: true }).catch(() => undefined) }, [loadPlan])
  useEffect(() => { if (allowed) refresh() }, [allowed, refresh])

  const setTab = (t: string) => {
    const next = new URLSearchParams(params)
    if (t === 'ledger') next.delete('tab')
    else next.set('tab', t)
    setParams(next, { replace: true })
  }

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Checking access…</div>
  }
  if (accessQuery.error || !allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Cash Planning is open to the people who manage bank details.</p>
          <p className="text-xs text-muted-foreground">
            {accessQuery.error ?? 'If you should be one of them, ask an admin to switch on can_manage_bank_details for you in cockpit access.'}
          </p>
          {accessQuery.error && <Button size="sm" variant="outline" onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>}
        </div>
      </div>
    )
  }

  const plan = planQuery.data
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-card px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Wallet className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 basis-60">
          <h1 className="text-lg font-semibold text-foreground">Cash Planning</h1>
          <p className="text-xs text-muted-foreground">
            FNB Gold Business Account. Received and earned money is committed. Forecast money is shown lighter and never counts toward payroll cover.
          </p>
        </div>
        {plan && <span className="text-xs text-muted-foreground">Worked out {formatSast(plan.as_at)}</span>}
        <Button size="sm" variant="outline" disabled={planQuery.loading} onClick={refresh}>
          <RefreshCw className={planQuery.loading ? 'mr-1 h-4 w-4 animate-spin' : 'mr-1 h-4 w-4'} /> Recalculate
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl space-y-4 p-3 sm:p-4">
          {planQuery.error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
              {planQuery.error}
            </div>
          )}
          {!plan && planQuery.loading && (
            <p className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Working out the plan…</p>
          )}
          {plan?.no_balance && (
            <div className="rounded-lg border border-border bg-card p-6 text-sm">
              <p>{plan.message}</p>
              <Button className="mt-3" size="sm" variant="brand" onClick={() => setBalanceOpen(true)}>Enter balance as at</Button>
            </div>
          )}
          {plan && !plan.no_balance && (
            <>
              <TopStrip plan={plan} onEnterBalance={() => setBalanceOpen(true)} />

              {plan.petrol_warning && (
                <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Petrol is planned on {plan.petrol.basis === 'band' ? 'the band check' : 'actual Sasol spend'}, and the two measures disagree by{' '}
                    {Math.abs(plan.petrol.disagreement_pct ?? 0)}%. See Variance for both, and Settings to change which the plan uses.
                  </span>
                </div>
              )}

              <Tabs value={tab} onValueChange={setTab}>
                <div className="overflow-x-auto">
                  <TabsList>
                    <TabsTrigger value="ledger">Day by day</TabsTrigger>
                    <TabsTrigger value="weekly">Weekly</TabsTrigger>
                    <TabsTrigger value="variance">Variance</TabsTrigger>
                    <TabsTrigger value="plan">The plan{plan.recurring_detected.length > 0 ? ` (${plan.recurring_detected.length} to review)` : ''}</TabsTrigger>
                    <TabsTrigger value="settings">Settings</TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="ledger" className="mt-3">
                  <Ledger days={plan.ledger} buffer={plan.settings.buffer_zar} onEdit={(item, date) => setEditing({ item, date })} />
                </TabsContent>
                <TabsContent value="weekly" className="mt-3">
                  <Weekly weeks={plan.weekly} basis={plan.scheduled_shifts?.basis ?? null} />
                </TabsContent>
                <TabsContent value="variance" className="mt-3 space-y-4">
                  {plan.covered_to && (
                    <p className="text-xs text-muted-foreground">Compared up to the last loaded statement, {formatDayYear(plan.covered_to)}.</p>
                  )}
                  <UberVariance rows={plan.uber_variance} tolerance={plan.settings.uber_tolerance_pct} />
                  <PetrolPattern petrol={plan.petrol} warning={plan.petrol_warning} />
                  <DebitVariance rows={plan.debit_variance} />
                </TabsContent>
                <TabsContent value="plan" className="mt-3 space-y-4">
                  <RecurringDetected rows={plan.recurring_detected} onAdd={(d) => setRowDraft(d)} onSaved={refresh} />
                  <PlanTable rows={plan.schedule} onEdit={(r) => setRowDraft(r)} onAdd={() => setRowDraft({ supplier: '' })} />
                  <FreshnessList rows={plan.freshness} holidaysTo={plan.holidays_to} coveredTo={plan.covered_to} />
                </TabsContent>
                <TabsContent value="settings" className="mt-3">
                  <SettingsForm settings={plan.settings} onSaved={refresh} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>

      <BalanceDialog open={balanceOpen} onOpenChange={setBalanceOpen} today={plan?.today ?? new Date().toISOString().slice(0, 10)} onSaved={refresh} />
      <OverrideDialog item={editing?.item ?? null} date={editing?.date ?? null} onClose={() => setEditing(null)} onSaved={refresh} />
      <RowDialog draft={rowDraft} onClose={() => setRowDraft(null)} onSaved={refresh} />
    </div>
  )
}
